/**
 * The devices the preview can be framed in (US 359, phase one).
 *
 * The frame is workbench chrome, not something the prototype draws. That is the whole point: a
 * generated mobile app is a plain full-bleed app, `position: fixed` works, shadcn's overlays work,
 * and what a founder opens on their own phone has no bezel painted inside it.
 *
 * `safe` is injected into the preview document as `--ideoz-safe-top` / `--ideoz-safe-bottom`
 * (see `previewDocument.ts`). Without it `env(safe-area-inset-*)` is 0 in a desktop iframe, so the
 * notch and the home indicator would exist on the founder's phone and not in the editor they
 * reviewed the layout in.
 */

export interface DevicePreset {
  id: string;
  label: string;
  /** null for the desktop preset: the iframe simply fills the panel. */
  width: number | null;
  height: number | null;
  safe: { top: number; bottom: number } | null;
  platform?: 'ios' | 'material';
  /**
   * The hardware drawn around the screen, in device pixels: a bezel, the screen's corner radius,
   * and what sits in the safe areas. Without it the frame is a plain rounded rectangle, and the
   * safe areas the app correctly leaves free read as empty space (28 Sep 2026).
   */
  hardware?: {
    bezel: number;
    screenRadius: number;
    island?: { width: number; height: number; top: number };
    homeIndicator?: { width: number; height: number; bottom: number };
  };
}

export const DEVICES: readonly DevicePreset[] = [
  { id: 'desktop', label: 'Desktop', width: null, height: null, safe: null },
  {
    id: 'iphone-17',
    label: 'iPhone 17',
    width: 402,
    height: 874,
    safe: { top: 59, bottom: 34 },
    platform: 'ios',
    hardware: {
      bezel: 12,
      screenRadius: 55,
      island: { width: 124, height: 36, top: 11 },
      homeIndicator: { width: 134, height: 5, bottom: 8 },
    },
  },
  {
    id: 'iphone-se',
    label: 'iPhone SE',
    width: 375,
    height: 667,
    safe: { top: 20, bottom: 0 },
    platform: 'ios',
  },
  {
    id: 'pixel-9',
    label: 'Pixel 9',
    width: 412,
    height: 915,
    // The status bar clears the punch-hole camera; the bottom is the gesture bar.
    safe: { top: 40, bottom: 24 },
    platform: 'material',
    hardware: {
      bezel: 10,
      screenRadius: 40,
      // A punch-hole camera: an island as wide as it is tall is drawn as a circle.
      island: { width: 22, height: 22, top: 11 },
      homeIndicator: { width: 108, height: 4, bottom: 8 },
    },
  },
  {
    id: 'ipad-mini',
    label: 'iPad mini',
    width: 744,
    height: 1133,
    safe: { top: 24, bottom: 20 },
    platform: 'ios',
  },
];

export const DESKTOP_DEVICE = DEVICES[0];

export const deviceById = (id: string): DevicePreset =>
  DEVICES.find((device) => device.id === id) ?? DESKTOP_DEVICE;

/**
 * The zoom that fits a device into the space available, rounded down to a tidy step so the label
 * reads as a choice rather than as a measurement. Never above 1: a phone blown up past life size
 * misleads about type sizes.
 */
export function fitZoom(device: DevicePreset, availableHeight: number, availableWidth: number): number {
  if (!device.height || !device.width) return 1;
  const bezel = 2 * (device.hardware?.bezel ?? 0);
  const raw = Math.min(
    availableHeight / (device.height + bezel + 24),
    availableWidth / (device.width + bezel + 24),
    1,
  );
  return Math.max(0.5, Math.floor(raw * 20) / 20);
}

/** Where the screen and the hardware go, in CSS pixels at `zoom`. */
export interface DeviceFrameLayout {
  /** The whole device, bezel included. */
  outer: { width: number; height: number };
  /** The screen's top-left inside it: where the (scaled) iframe is placed. */
  screen: { left: number; top: number; width: number; height: number; radius: number };
  /** Rounded corners of the body; null when the device has no bezel. */
  bodyRadius: number | null;
  island: { left: number; top: number; width: number; height: number } | null;
  homeIndicator: { left: number; top: number; width: number; height: number } | null;
}

/**
 * The frame for a device at `zoom`. `withSafeAreaHardware` is false where the app cannot be told
 * about the safe areas (a cross-origin published page): the app then starts at the very top, and
 * an island drawn there would cover its navbar.
 */
export function deviceFrameLayout(
  device: DevicePreset,
  zoom: number,
  withSafeAreaHardware = true,
): DeviceFrameLayout {
  const width = device.width ?? 0;
  const height = device.height ?? 0;
  const hw = device.hardware;
  const bezel = hw?.bezel ?? 0;
  const screen = {
    left: bezel * zoom,
    top: bezel * zoom,
    width: width * zoom,
    height: height * zoom,
    radius: (hw?.screenRadius ?? 28) * zoom,
  };
  const island =
    hw?.island && withSafeAreaHardware
      ? {
          width: hw.island.width * zoom,
          height: hw.island.height * zoom,
          top: screen.top + hw.island.top * zoom,
          left: screen.left + ((width - hw.island.width) / 2) * zoom,
        }
      : null;
  const homeIndicator =
    hw?.homeIndicator && withSafeAreaHardware
      ? {
          width: hw.homeIndicator.width * zoom,
          height: hw.homeIndicator.height * zoom,
          top: screen.top + (height - hw.homeIndicator.bottom - hw.homeIndicator.height) * zoom,
          left: screen.left + ((width - hw.homeIndicator.width) / 2) * zoom,
        }
      : null;
  return {
    outer: { width: (width + 2 * bezel) * zoom, height: (height + 2 * bezel) * zoom },
    screen,
    bodyRadius: hw ? (hw.screenRadius + bezel) * zoom : null,
    island,
    homeIndicator,
  };
}
